import React, { useState, useCallback, useEffect } from 'react';
import get from 'lodash/get';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Flex, Button, Input, Text, Wrapper, NakedButton, Icon } from 'roo-ui/components';
import Decimal from 'decimal.js';
import checkVoucher from 'lib/clients/checkVoucher';
import { getVoucherErrors } from 'store/booking/bookingSelectors';
import { getTotalPayableAtBooking, getPropertyId } from 'store/quote/quoteSelectors';
import { getVoucherAmount } from 'store/checkout/checkoutSelectors';
import { updatePayments } from 'store/checkout/checkoutActions';
import Currency from 'components/Currency';
import { FieldLabel, FieldError } from 'components/Form';
import VoucherTerms from './VoucherTerms';
import BookingError from 'pages/Checkout/BookingError';
import { PaymentMethodSummaryButton } from 'pages/Checkout/CustomerDetails/Steps/PaymentOptions/PaymentMethodSummary';
import { useDataLayer } from 'hooks/useDataLayer';

const STATES = {
  INITIAL: 'initial',
  EDITING: 'editing',
  ALLOCATED: 'allocated',
};

const HotelsVoucher = React.memo(({ ...rest }) => {
  const dispatch = useDispatch();
  const voucherAmount = useSelector(getVoucherAmount);
  const totalPayableAtBooking = useSelector(getTotalPayableAtBooking);
  const propertyId = useSelector(getPropertyId);
  const [state, setState] = useState(voucherAmount.greaterThan(0) ? STATES.ALLOCATED : STATES.INITIAL);
  const [voucherCode, setVoucherCode] = useState();
  const [error, setError] = useState(null);
  const voucherErrors = useSelector(getVoucherErrors);
  const { emitInteractionEvent } = useDataLayer();

  const addVoucher = useCallback(() => {
    setState(STATES.EDITING);
    emitInteractionEvent({ type: 'Voucher Link', value: 'Add Button Selected' });
  }, [emitInteractionEvent]);

  const applyVoucher = useCallback(async () => {
    try {
      const { amount } = await checkVoucher({ voucherCode, totalPayableAtBooking, propertyId });
      const amountToAllocate = Decimal.min(amount, totalPayableAtBooking);
      dispatch(updatePayments({ voucher: { code: voucherCode, amount: amountToAllocate } }));
      setState(STATES.ALLOCATED);
      emitInteractionEvent({ type: 'Voucher Link', value: 'Voucher Succeeded' });
    } catch (e) {
      const errorMessage = get(e, 'response.data.error', 'Invalid voucher code');
      setError({ message: errorMessage });
      emitInteractionEvent({ type: 'Voucher Link', value: 'Voucher Invalid' });
    }
  }, [dispatch, emitInteractionEvent, propertyId, totalPayableAtBooking.toNumber(), voucherCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeVoucher = useCallback(() => {
    dispatch(updatePayments({ voucher: { code: null, amount: new Decimal(0) } }));
    setVoucherCode(null);
    setState(STATES.INITIAL);
  }, [dispatch]);

  const cancelEdit = useCallback(() => {
    setVoucherCode(null);
    setState(STATES.INITIAL);
  }, []);

  const handleVoucherCodeChange = useCallback((value) => {
    setVoucherCode(value);
    setError(null);
  }, []);

  useEffect(() => {
    if (voucherErrors.length) {
      setState(STATES.EDITING);
      dispatch(updatePayments({ voucher: { code: null, amount: new Decimal(0) } }));
    }
  }, [dispatch, voucherErrors.length]);

  const showVoucherAmount = state === STATES.ALLOCATED && voucherAmount && !voucherAmount.isZero();

  return (
    <Wrapper {...rest}>
      <Flex
        justifyContent="space-between"
        padding={4}
        borderColor="greys.alto"
        border={1}
        borderBottom={state === STATES.EDITING ? 0 : 1}
        borderRadius={state === STATES.EDITING ? 'exageratedRoundTopOnly' : 'exagerated'}
      >
        <Flex justifyContent="space-between" width="100%" alignItems="center">
          <Flex justifyContent="space-between" flexDirection={['column', 'row']} alignItems={['flex-start', 'center']} width="100%">
            <Text fontWeight="bold" fontSize={['sm', 'base']}>
              Qantas Hotels voucher
            </Text>
            <Box mr={[0, 4]}>
              {showVoucherAmount && <Currency amount={voucherAmount} currency="AUD" fontSize="sm" data-testid="voucher-amount" />}
            </Box>
          </Flex>
          {state === STATES.INITIAL && (
            <PaymentMethodSummaryButton onClick={addVoucher} variant="primary" data-testid="add-voucher-button">
              ADD
            </PaymentMethodSummaryButton>
          )}
          {state === STATES.EDITING && (
            <NakedButton onClick={cancelEdit} data-testid="cancel-edit-voucher-button">
              <Icon name="close" color="greys.steel" />
            </NakedButton>
          )}
          {state === STATES.ALLOCATED && (
            <PaymentMethodSummaryButton onClick={removeVoucher} data-testid="remove-voucher-button">
              REMOVE
            </PaymentMethodSummaryButton>
          )}
        </Flex>
      </Flex>
      {state === STATES.EDITING && (
        <Box padding={4} borderColor="greys.alto" border={1} borderRadius="exageratedRoundBottomOnly">
          <BookingError errors={voucherErrors} type="voucher" />
          <FieldLabel htmlFor="voucherCode">Credit note or voucher code</FieldLabel>
          <FieldError error={error} mb={3} />
          <Flex flexDirection={['column', 'row']}>
            <Box mr={[0, 2]}>
              <Input
                id="voucherCode"
                initialValue={voucherCode}
                name="voucherCode"
                onChange={(e) => handleVoucherCodeChange(e.target.value)}
              />
            </Box>
            <Box mr={[0, 8]}>
              <Button
                disabled={!voucherCode}
                onClick={applyVoucher}
                variant="primary"
                width={['100%', 'auto']}
                data-testid="apply-voucher-button"
              >
                Apply
              </Button>
            </Box>
            <VoucherTerms />
          </Flex>
        </Box>
      )}
    </Wrapper>
  );
});

HotelsVoucher.displayName = 'HotelsVoucher';

export default HotelsVoucher;
